import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, API } from '../../App';
import { useDateTimeFormat } from '../../hooks/useDateTimeFormat';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, MapPin, Clock, Filter, Search, Navigation, UserCheck, Flag, CheckCircle2, RotateCcw, CalendarDays, History, Copy, ChevronDown, ChevronLeft, ChevronRight, X, Eye, Calendar as CalendarIcon, FileText, Upload, Download, File, Loader2, Radio, Zap, ZapOff, AlertTriangle, Car, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import PastRidesCalendar from './PastRidesCalendar';
import RideDetailModal from './RideDetailModal';
import RidesImportDialog from './RidesImportDialog';
import InlineDriverPicker from './InlineDriverPicker';
import InlineVehiclePicker from './InlineVehiclePicker';
import { Calendar } from '@/components/ui/calendar';
import useGeocoding from '../../hooks/useGeocoding';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import MobileRideCard from '../shared/MobileRideCard';


// Authenticated file download handler
const downloadVoucherWithAuth = async (rideId, voucherId, filename, token) => {
  try {
    const response = await fetch(`${API}/rides/${rideId}/vouchers/${voucherId}/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'voucher';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the object URL
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading voucher:', error);
    throw error;
  }
};

const downloadPortFileWithAuth = async (rideId, portFileId, filename, token) => {
  try {
    const response = await fetch(`${API}/rides/${rideId}/port-files/${portFileId}/download`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'port_file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading port file:', error);
    throw error;
  }
};

export default function RidesManagement() {
  const { user, token } = useAuth();
  const canEditRides = user?.section_permissions?.rides_edit !== false;
  const canActOnOthers = user?.section_permissions?.rides_actions_others !== false;
  const canPressActions = (ride) => canActOnOthers || ride.assigned_driver_id === user?.id;
  const { formatDateTime } = useDateTimeFormat();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [registeredPartners, setRegisteredPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingRide, setEditingRide] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDriver, setFilterDriver] = useState('all');
  const [filterPartner, setFilterPartner] = useState('all');
  const [filterVehicle, setFilterVehicle] = useState('all');
  const [filterPlate, setFilterPlate] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [filterEmptyField, setFilterEmptyField] = useState('none');
  const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState('');
  const partnerDropdownRef = useRef(null);
  
  // Past rides date filter
  const [pastRidesDateFilter, setPastRidesDateFilter] = useState('');
  const [showPastRidesCalendar, setShowPastRidesCalendar] = useState(false);
  const [pastRidesMonthFilter, setPastRidesMonthFilter] = useState(''); // Format: 'YYYY-MM'
  
  // Tab navigation for ride categories
  const [activeTab, setActiveTab] = useState('today'); // 'today', 'tomorrow', 'future', 'past'
  
  // Future rides month/year filter
  const [futureMonth, setFutureMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [futureYear, setFutureYear] = useState(new Date().getFullYear());
  
  // Month names for dropdown
  const monthNames = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];
  
  // Years for dropdown (from current year to +5 years for future rides)
  const currentYear = new Date().getFullYear();
  const futureYears = Array.from({ length: 6 }, (_, i) => currentYear + i);
  
  // Future month navigation functions
  const goToPreviousFutureMonth = () => {
    if (futureMonth === 1) {
      setFutureMonth(12);
      setFutureYear(futureYear - 1);
    } else {
      setFutureMonth(futureMonth - 1);
    }
  };
  
  const goToNextFutureMonth = () => {
    if (futureMonth === 12) {
      setFutureMonth(1);
      setFutureYear(futureYear + 1);
    } else {
      setFutureMonth(futureMonth + 1);
    }
  };

  // Close partner dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(event.target)) {
        setPartnerDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rideToDelete, setRideToDelete] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [rideToReset, setRideToReset] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRideForDetail, setSelectedRideForDetail] = useState(null);
  
  // Voucher state
  const [editingRideVouchers, setEditingRideVouchers] = useState([]);
  const [uploadingVoucher, setUploadingVoucher] = useState(false);
  const [editingRidePortFiles, setEditingRidePortFiles] = useState([]);
  const [uploadingPortFile, setUploadingPortFile] = useState(false);
  // Configurable vehicle types
  const [vehicleTypes, setVehicleTypes] = useState(['Sedan', 'SUV', 'Van', 'Luxury']);
  const [showManageVehicleTypes, setShowManageVehicleTypes] = useState(false);
  const [vehicleTypesDraft, setVehicleTypesDraft] = useState([]);
  const [newVehicleTypeInput, setNewVehicleTypeInput] = useState('');
  const [savingVehicleTypes, setSavingVehicleTypes] = useState(false);
  const voucherInputRef = useRef(null);
  const portFileInputRef = useRef(null);
  
  // Geocoding for auto-filling GPS coordinates
  const { geocodeAddressDebounced, isGeocoding } = useGeocoding();
  const [geocodingPickup, setGeocodingPickup] = useState(false);
  const [geocodingDropoff, setGeocodingDropoff] = useState(false);
  const [detectedPickupLocation, setDetectedPickupLocation] = useState('');
  const [detectedDropoffLocation, setDetectedDropoffLocation] = useState('');

  // Auto-geocode pickup location when it changes
  const handlePickupLocationChange = useCallback(async (value) => {
    // Auto-detect airport pickup
    const isAirport = value && value.toLowerCase().includes('airport');
    setFormData(prev => ({ ...prev, pickup_location: value, is_airport_pickup: isAirport ? true : prev.is_airport_pickup }));
    setDetectedPickupLocation(''); // Clear detected location while typing
    
    // Only geocode if address is reasonably long
    if (value && value.trim().length >= 5) {
      setGeocodingPickup(true);
      const result = await geocodeAddressDebounced(value, 1000);
      if (result) {
        setFormData(prev => ({
          ...prev,
          pickup_lat: result.lat.toString(),
          pickup_lng: result.lng.toString()
        }));
        // Show the detected location name for verification
        setDetectedPickupLocation(result.displayName);
      }
      setGeocodingPickup(false);
    }
  }, [geocodeAddressDebounced]);

  // Auto-geocode dropoff location when it changes
  const handleDropoffLocationChange = useCallback(async (value) => {
    setFormData(prev => ({ ...prev, dropoff_location: value }));
    setDetectedDropoffLocation(''); // Clear detected location while typing
    
    // Only geocode if address is reasonably long
    if (value && value.trim().length >= 5) {
      setGeocodingDropoff(true);
      const result = await geocodeAddressDebounced(value, 1000);
      if (result) {
        setFormData(prev => ({
          ...prev,
          dropoff_lat: result.lat.toString(),
          dropoff_lng: result.lng.toString()
        }));
        // Show the detected location name for verification
        setDetectedDropoffLocation(result.displayName);
      }
      setGeocodingDropoff(false);
    }
  }, [geocodeAddressDebounced]);

  const openDetailModal = (ride) => {
    setSelectedRideForDetail(ride);
    setDetailModalOpen(true);
  };

  // Voucher upload handler
  const handleVoucherUpload = async (file) => {
    if (!editingRide?.id) {
      toast.error('Please save the ride first before uploading vouchers');
      return;
    }
    
    setUploadingVoucher(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(
        `${API}/rides/${editingRide.id}/vouchers`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setEditingRideVouchers([...editingRideVouchers, response.data.voucher]);
      toast.success('Voucher uploaded successfully');
      fetchData(); // Refresh rides
    } catch (error) {
      console.error('Voucher upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload voucher');
    } finally {
      setUploadingVoucher(false);
    }
  };

  // Voucher delete handler
  const handleVoucherDelete = async (voucherId) => {
    if (!editingRide?.id) return;
    
    try {
      await axios.delete(`${API}/rides/${editingRide.id}/vouchers/${voucherId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditingRideVouchers(editingRideVouchers.filter(v => v.id !== voucherId));
      toast.success('Voucher deleted');
      fetchData(); // Refresh rides
    } catch (error) {
      toast.error('Failed to delete voucher');
    }
  };

  // Port file upload handler
  const handlePortFileUpload = async (file) => {
    if (!editingRide?.id) {
      toast.error('Please save the ride first before uploading port files');
      return;
    }
    setUploadingPortFile(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(
        `${API}/rides/${editingRide.id}/port-files`,
        formDataUpload,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setEditingRidePortFiles([...editingRidePortFiles, response.data.port_file]);
      toast.success('Port file uploaded successfully');
      fetchData();
    } catch (error) {
      console.error('Port file upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload port file');
    } finally {
      setUploadingPortFile(false);
    }
  };

  // Port file delete handler
  const handlePortFileDelete = async (portFileId) => {
    if (!editingRide?.id) return;
    try {
      await axios.delete(`${API}/rides/${editingRide.id}/port-files/${portFileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingRidePortFiles(editingRidePortFiles.filter(v => v.id !== portFileId));
      toast.success('Port file deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete port file');
    }
  };

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    number_of_people: '',
    flight_number: '',
    booking_number: '',
    date_time: '',
    pickup_location: '',
    dropoff_location: '',
    // GPS coordinates for geofencing
    pickup_lat: '',
    pickup_lng: '',
    dropoff_lat: '',
    dropoff_lng: '',
    vehicle_type: '',
    price: '',
    payment_to_partner: '',
    driver_commission: '',
    commission_driver_id: '',
    notes: '',
    partner: '',
    partner_id: '',
    assigned_driver_id: 'unassigned',
    vehicle_id: '',
    status: 'active',
    is_airport_pickup: false,
    extras: [],
    pos_taxable: true  // Only meaningful when partner=POS. Defaults to true (24% tax on).
  });
  const [vehicles, setVehicles] = useState([]);

  // Extras catalog state
  const [extrasCatalog, setExtrasCatalog] = useState([]);
  const [newExtraInput, setNewExtraInput] = useState('');

  const fetchExtrasCatalog = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/extras`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExtrasCatalog(response.data.extras || []);
    } catch (error) {
      console.error('Error fetching extras catalog:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    fetchExtrasCatalog();
    // Load configurable vehicle types
    (async () => {
      try {
        const res = await axios.get(`${API}/settings/vehicle-types`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (Array.isArray(res.data?.value) && res.data.value.length) {
          setVehicleTypes(res.data.value);
        }
      } catch (e) { /* keep defaults */ }
    })();
  }, []);

  // Manage vehicle types (admin only)
  const openManageVehicleTypes = () => {
    setVehicleTypesDraft([...vehicleTypes]);
    setNewVehicleTypeInput('');
    setShowManageVehicleTypes(true);
  };

  const saveVehicleTypes = async () => {
    if (!vehicleTypesDraft.length) {
      toast.error('At least one vehicle type is required');
      return;
    }
    setSavingVehicleTypes(true);
    try {
      const res = await axios.put(`${API}/settings/vehicle-types`,
        { value: vehicleTypesDraft },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = res.data?.value || vehicleTypesDraft;
      setVehicleTypes(updated);
      setShowManageVehicleTypes(false);
      toast.success('Vehicle types updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update vehicle types');
    } finally {
      setSavingVehicleTypes(false);
    }
  };

  const fetchData = async () => {
    try {
      const [ridesRes, driversRes, partnersRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/rides`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/drivers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/partners`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })), // Handle if partners endpoint doesn't exist
        axios.get(`${API}/vehicles`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })) // Handle if vehicles endpoint doesn't exist
      ]);
      setRides(ridesRes.data);
      setDrivers(driversRes.data);
      setRegisteredPartners(partnersRes.data || []);
      setVehicles(vehiclesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit query parameter from URL (e.g., from Commission list or Past Rides)
  useEffect(() => {
    const editRideId = searchParams.get('edit');
    const duplicateRideId = searchParams.get('duplicate');
    
    // Helper to preserve return params while clearing edit/duplicate
    const clearEditParams = () => {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('edit');
      newParams.delete('duplicate');
      setSearchParams(newParams);
    };
    
    const handleUrlParams = async () => {
      if (editRideId) {
        // Try to find ride in current list first
        let rideToEdit = rides.find(r => r.id === editRideId);
        
        // If not found (e.g., it's a past ride), fetch from API
        if (!rideToEdit) {
          try {
            const response = await axios.get(`${API}/rides/${editRideId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            rideToEdit = response.data;
          } catch (error) {
            console.error('Error fetching ride for edit:', error);
            toast.error('Ride not found');
            clearEditParams();
            return;
          }
        }
        
        if (rideToEdit) {
          openEditDialog(rideToEdit);
          clearEditParams();
        }
      } else if (duplicateRideId) {
        // Try to find ride in current list first
        let rideToDuplicate = rides.find(r => r.id === duplicateRideId);
        
        // If not found (e.g., it's a past ride), fetch from API
        if (!rideToDuplicate) {
          try {
            const response = await axios.get(`${API}/rides/${duplicateRideId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            rideToDuplicate = response.data;
          } catch (error) {
            console.error('Error fetching ride for duplicate:', error);
            toast.error('Ride not found');
            clearEditParams();
            return;
          }
        }
        
        if (rideToDuplicate) {
          openDuplicateDialog(rideToDuplicate);
          clearEditParams();
        }
      }
    };
    
    if (editRideId || duplicateRideId) {
      handleUrlParams();
    }
  }, [searchParams, rides, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const price = formData.price ? parseFloat(formData.price) : null;
      if (formData.price && isNaN(price)) {
        toast.error('Please enter a valid price');
        return;
      }
      
      const payment_to_partner = formData.payment_to_partner ? parseFloat(formData.payment_to_partner) : null;
      if (formData.payment_to_partner && isNaN(payment_to_partner)) {
        toast.error('Please enter a valid payment to partner amount');
        return;
      }
      
      const driver_commission = formData.driver_commission ? parseFloat(formData.driver_commission) : null;
      if (formData.driver_commission && isNaN(driver_commission)) {
        toast.error('Please enter a valid driver commission');
        return;
      }
      
      const number_of_people = formData.number_of_people ? parseInt(formData.number_of_people) : null;
      
      // Parse GPS coordinates
      const pickup_lat = formData.pickup_lat ? parseFloat(formData.pickup_lat) : null;
      const pickup_lng = formData.pickup_lng ? parseFloat(formData.pickup_lng) : null;
      const dropoff_lat = formData.dropoff_lat ? parseFloat(formData.dropoff_lat) : null;
      const dropoff_lng = formData.dropoff_lng ? parseFloat(formData.dropoff_lng) : null;
      
      // Clean up payload: convert empty strings to null
      const payload = {
        customer_name: formData.customer_name || null,
        customer_phone: formData.customer_phone || null,
        number_of_people: number_of_people,
        flight_number: formData.flight_number || null,
        booking_number: formData.booking_number || null,
        date_time: formData.date_time ? new Date(formData.date_time).toISOString() : null,
        pickup_location: formData.pickup_location || null,
        dropoff_location: formData.dropoff_location || null,
        pickup_lat: pickup_lat,
        pickup_lng: pickup_lng,
        dropoff_lat: dropoff_lat,
        dropoff_lng: dropoff_lng,
        vehicle_type: formData.vehicle_type || null,
        price: price,
        payment_to_partner: payment_to_partner,
        driver_commission: driver_commission,
        commission_driver_id: formData.commission_driver_id || null,
        notes: formData.notes || null,
        partner: formData.partner || null,
        partner_id: formData.partner_id || null,
        assigned_driver_id: formData.assigned_driver_id === 'unassigned' ? null : formData.assigned_driver_id || null,
        vehicle_id: formData.vehicle_id || null,
        status: formData.status || 'active',
        is_airport_pickup: formData.is_airport_pickup || false,
        extras: formData.extras && formData.extras.length > 0 ? formData.extras : null,
        pos_taxable: formData.pos_taxable !== false
      };

      if (editingRide) {
        await axios.put(`${API}/rides/${editingRide.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ride updated successfully');
      } else {
        await axios.post(`${API}/rides`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Ride created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
      fetchExtrasCatalog();
      
      // Check if we need to return to Past Rides
      const returnTo = searchParams.get('returnTo');
      if (returnTo === 'past-rides') {
        // Build the return URL with preserved filters
        const returnUrl = new URLSearchParams();
        const month = searchParams.get('month');
        const year = searchParams.get('year');
        const filterStatus = searchParams.get('filterStatus');
        const filterDriver = searchParams.get('filterDriver');
        const filterPartner = searchParams.get('filterPartner');
        const filterVehicle = searchParams.get('filterVehicle');
        const searchTerm = searchParams.get('searchTerm');
        const page = searchParams.get('page');
        const filterEmptyFieldParam = searchParams.get('filterEmptyField');
        
        if (month) returnUrl.set('month', month);
        if (year) returnUrl.set('year', year);
        if (filterStatus) returnUrl.set('filterStatus', filterStatus);
        if (filterDriver) returnUrl.set('filterDriver', filterDriver);
        if (filterPartner) returnUrl.set('filterPartner', filterPartner);
        if (filterVehicle) returnUrl.set('filterVehicle', filterVehicle);
        if (searchTerm) returnUrl.set('searchTerm', searchTerm);
        if (page) returnUrl.set('page', page);
        if (filterEmptyFieldParam) returnUrl.set('filterEmptyField', filterEmptyFieldParam);
        
        const queryString = returnUrl.toString();
        navigate(`/admin/past-rides${queryString ? '?' + queryString : ''}`);
        return;
      }
    } catch (error) {
      console.error('Error saving ride:', error);
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : error.response?.data?.message || error.message || 'Failed to save ride';
      toast.error(errorMessage);
    }
  };

  const openDeleteDialog = (ride) => {
    setRideToDelete(ride);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!rideToDelete) return;

    try {
      await axios.delete(`${API}/rides/${rideToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ride deleted successfully');
      setDeleteDialogOpen(false);
      setRideToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting ride:', error);
      toast.error('Failed to delete ride');
      setDeleteDialogOpen(false);
      setRideToDelete(null);
    }
  };

  const markArrivedAtPickup = async (rideId) => {
    try {
      await axios.put(`${API}/rides/${rideId}/arrived-at-pickup`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Arrived at pickup point!');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || (error.response ? `Failed to update (HTTP ${error.response.status})` : 'Server unreachable - check your connection'));
    }
  };

  const markPickedUpCustomer = async (rideId) => {
    try {
      await axios.put(`${API}/rides/${rideId}/picked-up-customer`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Customer picked up!');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || (error.response ? `Failed to update (HTTP ${error.response.status})` : 'Server unreachable - check your connection'));
    }
  };

  const markArrivedAtDropoff = async (rideId) => {
    try {
      await axios.put(`${API}/rides/${rideId}/arrived-at-dropoff`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Arrived at dropoff - Ride completed!');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || (error.response ? `Failed to update (HTTP ${error.response.status})` : 'Server unreachable - check your connection'));
    }
  };

  // Toggle ride LIVE status
  const toggleRideLive = async (rideId, currentLiveStatus) => {
    try {
      const response = await axios.put(`${API}/rides/${rideId}/set-live`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Error toggling ride live status:', error);
      toast.error('Failed to update ride live status');
    }
  };

  // Set all tomorrow's rides as LIVE
  const setAllTomorrowRidesLive = async () => {
    try {
      const response = await axios.put(`${API}/rides/set-all-tomorrow-live`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Error setting all rides live:', error);
      toast.error('Failed to set rides live');
    }
  };

  // Set all tomorrow's rides as OFFLINE
  const setAllTomorrowRidesOffline = async () => {
    try {
      const response = await axios.put(`${API}/rides/set-all-tomorrow-offline`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Error setting all rides offline:', error);
      toast.error('Failed to set rides offline');
    }
  };

  const openResetDialog = (ride) => {
    setRideToReset(ride);
    setResetDialogOpen(true);
  };

  const resetTracking = async () => {
    if (!rideToReset) return;
    
    try {
      await axios.put(`${API}/rides/${rideToReset.id}/reset-tracking`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Tracking reset successfully!');
      setResetDialogOpen(false);
      setRideToReset(null);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to reset tracking');
      setResetDialogOpen(false);
      setRideToReset(null);
    }
  };

  const openEditDialog = (ride) => {
    setEditingRide(ride);
    const dateTime = new Date(ride.date_time);
    const localDateTime = new Date(dateTime.getTime() - dateTime.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    
    setFormData({
      customer_name: ride.customer_name || '',
      customer_phone: ride.customer_phone || '',
      number_of_people: ride.number_of_people ? ride.number_of_people.toString() : '',
      flight_number: ride.flight_number || '',
      booking_number: ride.booking_number || '',
      date_time: localDateTime,
      pickup_location: ride.pickup_location || '',
      dropoff_location: ride.dropoff_location || '',
      pickup_lat: ride.pickup_lat ? ride.pickup_lat.toString() : '',
      pickup_lng: ride.pickup_lng ? ride.pickup_lng.toString() : '',
      dropoff_lat: ride.dropoff_lat ? ride.dropoff_lat.toString() : '',
      dropoff_lng: ride.dropoff_lng ? ride.dropoff_lng.toString() : '',
      vehicle_type: ride.vehicle_type || '',
      price: ride.price ? ride.price.toString() : '',
      payment_to_partner: ride.payment_to_partner ? ride.payment_to_partner.toString() : '',
      driver_commission: ride.driver_commission ? ride.driver_commission.toString() : '',
      commission_driver_id: ride.commission_driver_id || '',
      notes: ride.notes || '',
      partner: ride.partner || '',
      partner_id: ride.partner_id || '',
      assigned_driver_id: ride.assigned_driver_id || 'unassigned',
      vehicle_id: ride.vehicle_id || '',
      status: ride.status || 'in_progress',
      is_airport_pickup: ride.is_airport_pickup || false,
      extras: ride.extras || [],
      pos_taxable: ride.pos_taxable !== false  // default to true for legacy/missing values
    });
    setEditingRideVouchers(ride.vouchers || []);
    setEditingRidePortFiles(ride.port_files || []);
    setDialogOpen(true);
  };

  const openDuplicateDialog = (ride) => {
    // Set editingRide to null so it creates a new ride instead of updating
    setEditingRide(null);
    const dateTime = new Date(ride.date_time);
    const localDateTime = new Date(dateTime.getTime() - dateTime.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    
    // Copy all ride data but allow editing
    setFormData({
      customer_name: ride.customer_name || '',
      customer_phone: ride.customer_phone || '',
      number_of_people: ride.number_of_people ? ride.number_of_people.toString() : '',
      flight_number: ride.flight_number || '',
      booking_number: ride.booking_number || '',
      date_time: localDateTime,
      pickup_location: ride.pickup_location || '',
      dropoff_location: ride.dropoff_location || '',
      pickup_lat: ride.pickup_lat ? ride.pickup_lat.toString() : '',
      pickup_lng: ride.pickup_lng ? ride.pickup_lng.toString() : '',
      dropoff_lat: ride.dropoff_lat ? ride.dropoff_lat.toString() : '',
      dropoff_lng: ride.dropoff_lng ? ride.dropoff_lng.toString() : '',
      vehicle_type: ride.vehicle_type || '',
      price: ride.price ? ride.price.toString() : '',
      payment_to_partner: ride.payment_to_partner ? ride.payment_to_partner.toString() : '',
      driver_commission: ride.driver_commission ? ride.driver_commission.toString() : '',
      commission_driver_id: ride.commission_driver_id || '',
      notes: ride.notes || '',
      partner: ride.partner || '',
      partner_id: ride.partner_id || '',
      assigned_driver_id: ride.assigned_driver_id || 'unassigned',
      vehicle_id: ride.vehicle_id || '',
      status: 'active', // New duplicated ride should be active by default
      is_airport_pickup: ride.is_airport_pickup || false,
      extras: ride.extras || [],
      pos_taxable: ride.pos_taxable !== false
    });
    setEditingRideVouchers([]); // Don't copy vouchers when duplicating
    setEditingRidePortFiles([]); // Don't copy port files when duplicating
    setDialogOpen(true);
    toast.info('Duplicating ride - modify details and save');
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      number_of_people: '',
      flight_number: '',
      date_time: '',
      pickup_location: '',
      dropoff_location: '',
      pickup_lat: '',
      pickup_lng: '',
      dropoff_lat: '',
      dropoff_lng: '',
      vehicle_type: '',
      price: '',
      payment_to_partner: '',
      driver_commission: '',
      commission_driver_id: '',
      notes: '',
      partner: '',
      partner_id: '',
      assigned_driver_id: 'unassigned',
      vehicle_id: '',
      status: 'in_progress',
      is_airport_pickup: false,
      extras: [],
      pos_taxable: true
    });
    setEditingRide(null);
    setPartnerDropdownOpen(false);
    setEditingRideVouchers([]);
    setEditingRidePortFiles([]);
    // Clear detected locations
    setDetectedPickupLocation('');
    setDetectedDropoffLocation('');
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-sky-100 text-sky-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-emerald-100 text-emerald-800',
      no_show: 'bg-amber-100 text-amber-800',
      driver_no_show: 'bg-red-200 text-red-900 border border-red-400',
      cancelled: 'bg-red-100 text-red-800',
      paid_cancelled: 'bg-orange-100 text-orange-800',
      free_cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'active',
      in_progress: 'in progress',
      completed: 'completed',
      no_show: 'no show',
      driver_no_show: 'driver no-show',
      cancelled: 'cancelled',
      paid_cancelled: 'paid cancelled',
      free_cancelled: 'free cancelled'
    };
    return labels[status] || status;
  };

  // Get unique partners and vehicle types for filter options
  const uniquePartners = [...new Set(rides.map(r => r.partner).filter(Boolean))].sort();
  const uniqueVehicles = [...new Set(rides.map(r => r.vehicle_type).filter(Boolean))].sort();
  const uniquePlates = [...new Set(rides.map(r => r.vehicle_plate).filter(Boolean))].sort();

  // Filter rides - shows ALL rides regardless of status in all tabs
  const filteredRides = rides.filter(ride => {
    // Cancelled rides toggle - hide all cancelled variants when toggle is off
    if (!includeCancelled && ['cancelled', 'free_cancelled', 'paid_cancelled'].includes(ride.status)) {
      return false;
    }
    
    // Status filter
    const matchesStatus = filterStatus === 'all' || ride.status === filterStatus;
    
    // Driver filter - exclude free_cancelled rides when filtering by specific driver
    const matchesDriver = filterDriver === 'all' 
      ? true 
      : (ride.assigned_driver_id === filterDriver && ride.status !== 'free_cancelled');
    
    // Partner filter
    const matchesPartner = filterPartner === 'all' || ride.partner === filterPartner;
    
    // Vehicle type filter
    const matchesVehicle = filterVehicle === 'all' || ride.vehicle_type === filterVehicle;
    
    // Number plate filter
    const matchesPlate = filterPlate === 'all' || ride.vehicle_plate === filterPlate;
    
    // Search term - searches across all text fields
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (ride.customer_name && ride.customer_name.toLowerCase().includes(searchLower)) ||
      (ride.customer_phone && ride.customer_phone.toLowerCase().includes(searchLower)) ||
      (ride.pickup_location && ride.pickup_location.toLowerCase().includes(searchLower)) ||
      (ride.dropoff_location && ride.dropoff_location.toLowerCase().includes(searchLower)) ||
      (ride.flight_number && ride.flight_number.toLowerCase().includes(searchLower)) ||
      (ride.notes && ride.notes.toLowerCase().includes(searchLower)) ||
      (ride.partner && ride.partner.toLowerCase().includes(searchLower)) ||
      (ride.vehicle_plate && ride.vehicle_plate.toLowerCase().includes(searchLower)) ||
      (ride.vehicle_type && ride.vehicle_type.toLowerCase().includes(searchLower));
    
    // Date range filter
    let matchesDateRange = true;
    if (ride.date_time) {
      const rideDate = new Date(ride.date_time);
      if (filterDateFrom) {
        const fromDate = new Date(filterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (rideDate < fromDate) matchesDateRange = false;
      }
      if (filterDateTo) {
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999);
        if (rideDate > toDate) matchesDateRange = false;
      }
    } else if (filterDateFrom || filterDateTo) {
      // If date filter is set but ride has no date, exclude it
      matchesDateRange = false;
    }
    
    // Price range filter
    let matchesPriceRange = true;
    if (filterPriceMin && ride.price !== null && ride.price !== undefined) {
      if (ride.price < parseFloat(filterPriceMin)) matchesPriceRange = false;
    }
    if (filterPriceMax && ride.price !== null && ride.price !== undefined) {
      if (ride.price > parseFloat(filterPriceMax)) matchesPriceRange = false;
    }
    
    // Empty field filter
    let matchesEmptyField = true;
    if (filterEmptyField !== 'none') {
      const val = ride[filterEmptyField];
      matchesEmptyField = val === null || val === undefined || val === '' || val === 0;
    }
    
    return matchesStatus && matchesDriver && matchesPartner && matchesVehicle && 
           matchesPlate && matchesSearch && matchesDateRange && matchesPriceRange && matchesEmptyField;
  });

  // Check if any advanced filters are active
  const hasActiveAdvancedFilters = filterPartner !== 'all' || filterVehicle !== 'all' || 
    filterPlate !== 'all' || filterDateFrom || filterDateTo || filterPriceMin || filterPriceMax || filterEmptyField !== 'none';

  // Clear all filters
  const clearAllFilters = () => {
    setFilterStatus('all');
    setFilterDriver('all');
    setFilterPartner('all');
    setFilterVehicle('all');
    setFilterPlate('all');
    setSearchTerm('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterPriceMin('');
    setFilterPriceMax('');
    setIncludeCancelled(false);
    setFilterEmptyField('none');
  };

  // Categorize rides into Today, Tomorrow, Future, Past, and Unscheduled
  const categorizeRides = (filteredRidesList) => {
    const now = new Date();
    
    // Use LOCAL date (not UTC) to correctly determine today/tomorrow boundaries
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    
    const tomorrowDate = new Date(now);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrowStr = `${tomorrowDate.getFullYear()}-${String(tomorrowDate.getMonth() + 1).padStart(2, '0')}-${String(tomorrowDate.getDate()).padStart(2, '0')}`;

    const todayRides = [];
    const tomorrowRides = [];
    const futureRides = [];
    const pastRides = [];
    const unscheduledRides = [];

    // Categorize all rides from filtered rides
    filteredRidesList.forEach(ride => {
      if (!ride.date_time) {
        unscheduledRides.push(ride);
        return;
      }
      
      // Convert ride date_time to LOCAL date for correct categorization
      // A ride at "2026-04-23T00:00:00" Athens stored as "2026-04-22T21:00:00Z" UTC
      // must be categorized as April 23 (local), not April 22 (UTC)
      const rideDate = new Date(ride.date_time);
      const rideDateStr = `${rideDate.getFullYear()}-${String(rideDate.getMonth() + 1).padStart(2, '0')}-${String(rideDate.getDate()).padStart(2, '0')}`;

      // IMPORTANT: Active or in_progress rides from past dates should appear in TODAY's rides
      // so they remain visible and actionable (not hidden in past rides)
      const isActiveOrInProgress = ride.status === 'active' || ride.status === 'in_progress';
      const isPastDate = rideDateStr < todayStr;
      
      if (isPastDate && isActiveOrInProgress) {
        // Past date but still active/in_progress - show in today's rides so it's visible
        todayRides.push(ride);
      } else if (rideDateStr === todayStr) {
        todayRides.push(ride);
      } else if (rideDateStr === tomorrowStr) {
        tomorrowRides.push(ride);
      } else if (rideDateStr > tomorrowStr) {
        futureRides.push(ride);
      } else if (isPastDate) {
        // Past date with completed/cancelled/no_show status - goes to past rides
        pastRides.push(ride);
      }
    });

    // Sort today's rides by status priority first, then by time
    // Priority: in_progress (1), active (2), no_show (3), delayed (4), others (5)
    const statusPriority = {
      'in_progress': 1,
      'active': 2,
      'no_show': 3,
      'delayed': 4,
      'paid_cancelled': 5,
      'free_cancelled': 6
    };
    
    todayRides.sort((a, b) => {
      const priorityA = statusPriority[a.status] || 5;
      const priorityB = statusPriority[b.status] || 5;
      
      // First sort by status priority
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      // Then by time within same status
      return new Date(a.date_time) - new Date(b.date_time);
    });
    
    // Sort tomorrow's rides by time
    tomorrowRides.sort((a, b) => new Date(a.date_time) - new Date(b.date_time));

    // Sort future rides: closest date first, then by time within each day
    futureRides.sort((a, b) => new Date(a.date_time) - new Date(b.date_time));
    
    // Sort past rides: most recent day first (yesterday, then day before, etc.)
    // Within each day, sort from 00:00 to 23:59
    pastRides.sort((a, b) => {
      const dateA = new Date(a.date_time);
      const dateB = new Date(b.date_time);
      const dayA = new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate());
      const dayB = new Date(dateB.getFullYear(), dateB.getMonth(), dateB.getDate());
      
      // First sort by day (most recent first)
      if (dayA.getTime() !== dayB.getTime()) {
        return dayB.getTime() - dayA.getTime(); // Descending by day
      }
      // Then sort by time within the day (00:00 to 23:59)
      return dateA.getTime() - dateB.getTime(); // Ascending by time
    });

    return { todayRides, tomorrowRides, futureRides, pastRides, unscheduledRides };
  };

  const { todayRides, tomorrowRides, futureRides, pastRides, unscheduledRides } = categorizeRides(filteredRides);

  // Active rides = driver already pressed at least "Arrived at Pickup" and ride not finished
  const activeNowRides = todayRides.filter(
    r => r.arrived_at_pickup && !['completed', 'cancelled', 'no_show'].includes(r.status)
  );
  const activeNowIds = new Set(activeNowRides.map(r => r.id));
  // Today's queue excludes rides already shown in the Active panel (no duplicates)
  const todayQueueRides = todayRides.filter(r => !activeNowIds.has(r.id));

  // Filter past rides by selected date or month
  const filteredPastRides = pastRides.filter(ride => {
    const rideDate = new Date(ride.date_time);
    
    // If date filter is set, filter by exact date
    if (pastRidesDateFilter) {
      const filterDate = new Date(pastRidesDateFilter);
      return (
        rideDate.getFullYear() === filterDate.getFullYear() &&
        rideDate.getMonth() === filterDate.getMonth() &&
        rideDate.getDate() === filterDate.getDate()
      );
    }
    
    // If month filter is set (and not 'all'), filter by month
    if (pastRidesMonthFilter && pastRidesMonthFilter !== 'all') {
      const [year, month] = pastRidesMonthFilter.split('-').map(Number);
      return (
        rideDate.getFullYear() === year &&
        rideDate.getMonth() === month - 1 // JavaScript months are 0-indexed
      );
    }
    
    // No filter, show all
    return true;
  });

  // Filter future rides by selected month/year
  const filteredFutureRides = futureRides.filter(ride => {
    const rideDate = new Date(ride.date_time);
    return (
      rideDate.getFullYear() === futureYear &&
      rideDate.getMonth() === futureMonth - 1 // JavaScript months are 0-indexed
    );
  });

  // Get available months from past rides for the month filter dropdown with ride counts
  const monthRideCounts = pastRides.reduce((acc, ride) => {
    const date = new Date(ride.date_time);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    acc[monthKey] = (acc[monthKey] || 0) + 1;
    return acc;
  }, {});
  
  const availableMonths = Object.keys(monthRideCounts).sort((a, b) => b.localeCompare(a)); // Sort descending (most recent first)

  // Sticky column styles for the first 5 columns
  const stickyBase = 'sticky z-10 bg-white';
  const stickyHeadBase = 'sticky z-20 bg-slate-50';
  const stickyStyles = [
    { left: 0, minWidth: 130 },        // Customer
    { left: 130, minWidth: 110 },       // Date & Time
    { left: 240, minWidth: 220 },       // Route
    { left: 460, minWidth: 110 },       // Driver
    { left: 570, minWidth: 120, shadow: true }, // Partner (last sticky)
  ];

  // Function to render ride rows
  const renderRideRow = (ride) => (
    <tr
      key={ride.id}
      className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors group"
      data-testid={`ride-row-${ride.id}`}
    >
      {/* 1. Customer (sticky) */}
      <td className={`px-6 py-4 text-sm font-medium text-slate-900 ${stickyBase} group-hover:bg-slate-50`} style={{ left: stickyStyles[0].left, minWidth: stickyStyles[0].minWidth }}>
        {ride.customer_name || 'N/A'}
      </td>
      {/* 2. Date & Time (sticky) */}
      <td className={`px-6 py-4 text-sm text-slate-600 font-mono ${stickyBase} group-hover:bg-slate-50`} style={{ left: stickyStyles[1].left, minWidth: stickyStyles[1].minWidth }}>
        {ride.date_time ? formatDateTime(ride.date_time) : 'N/A'}
      </td>
      {/* 3. Route (sticky) */}
      <td className={`px-6 py-4 text-sm text-slate-600 ${stickyBase} group-hover:bg-slate-50`} style={{ left: stickyStyles[2].left, minWidth: stickyStyles[2].minWidth }}>
        <div className="max-w-xs">
          <a 
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.pickup_location || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start text-blue-600 hover:text-blue-800 font-medium"
          >
            <MapPin className="h-4 w-4 text-blue-600 mr-1 mt-0.5 flex-shrink-0" />
            <span className="truncate">{ride.pickup_location || 'N/A'}</span>
          </a>
          <a 
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ride.dropoff_location || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start mt-1 text-red-600 hover:text-red-800 font-medium"
          >
            <MapPin className="h-4 w-4 text-red-600 mr-1 mt-0.5 flex-shrink-0" />
            <span className="truncate">{ride.dropoff_location || 'N/A'}</span>
          </a>
        </div>
      </td>
      {/* 4. Driver (sticky) */}
      <td className={`px-6 py-4 text-sm text-slate-600 ${stickyBase} group-hover:bg-slate-50`} style={{ left: stickyStyles[3].left, minWidth: stickyStyles[3].minWidth }} onClick={(e) => e.stopPropagation()}>
        <InlineDriverPicker
          ride={ride}
          drivers={drivers}
          onUpdated={(updated) => {
            setRides((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
          }}
        />
      </td>
      {/* 5. Partner (sticky - last, with shadow) */}
      <td className={`px-6 py-4 text-sm text-slate-600 ${stickyBase} group-hover:bg-slate-50`} style={{ left: stickyStyles[4].left, minWidth: stickyStyles[4].minWidth, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.08)' }}>
        {ride.partner ? (
          <div className="flex flex-col gap-1">
            <span>{ride.partner}</span>
            {!['CASH', 'POS'].includes(ride.partner) && ride.partner_id && !ride.payment_to_partner && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-medium border border-red-200 w-fit" data-testid="partner-price-missing-badge" title="Prezzo partner mancante">
                <AlertTriangle className="h-2.5 w-2.5" />
                Prezzo mancante
              </span>
            )}
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      {/* 6. Vehicle */}
      <td className="px-6 py-4 text-sm text-slate-600" onClick={(e) => e.stopPropagation()}>
        <InlineVehiclePicker
          ride={ride}
          vehicles={vehicles}
          onUpdated={(updated) => {
            setRides((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
          }}
        />
      </td>
      {/* 7. V. Type */}
      <td className="px-6 py-4 text-sm text-slate-600">
        {ride.vehicle_type ? (
          <span className="text-slate-700">{ride.vehicle_type}</span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      {/* 8. Extra */}
      <td className="px-6 py-4 text-sm text-slate-600">
        {ride.extras && ride.extras.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {ride.extras.map((extra) => (
              <span key={extra} className="bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded text-xs whitespace-nowrap">
                {extra}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      {/* 9. Price */}
      <td className="px-6 py-4 text-sm font-medium text-slate-900">
        €{ride.price != null ? ride.price.toFixed(2) : '0.00'}
      </td>
      {/* 10. Commission */}
      <td className="px-6 py-4 text-sm font-medium text-emerald-700">
        €{ride.driver_commission != null ? ride.driver_commission.toFixed(2) : '0.00'}
      </td>
      {/* 11. Status */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <span className={`status-badge ${getStatusColor(ride.status)}`}>
            {getStatusLabel(ride.status)}
          </span>
          {ride.is_live ? (
            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
              LIVE
            </span>
          ) : (
            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
              NOT LIVE
            </span>
          )}
        </div>
      </td>
      {/* 12. Tracking */}
      <td className="px-6 py-4 text-sm text-slate-600">
        {renderTrackingButtons(ride)}
      </td>
      {/* 13. Actions */}
      <td className="px-6 py-4 text-sm text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => openDetailModal(ride)}
            className="p-2 text-slate-600 hover:bg-slate-100 rounded-sm transition-colors"
            title="View details"
            data-testid={`view-ride-${ride.id}`}
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => toggleRideLive(ride.id, ride.is_live)}
            className={`p-2 rounded-sm transition-colors ${
              ride.is_live 
                ? 'text-green-600 hover:bg-green-50' 
                : 'text-gray-400 hover:bg-gray-100'
            }`}
            title={ride.is_live ? 'Click to set NOT LIVE' : 'Click to set LIVE'}
            data-testid={`toggle-live-${ride.id}`}
          >
            <Zap className="h-4 w-4" />
          </button>
          <button
            onClick={() => openDuplicateDialog(ride)}
            className="p-2 text-purple-600 hover:bg-purple-50 rounded-sm transition-colors"
            title="Duplicate ride"
          >
            <Copy className="h-4 w-4" />
          </button>
          {canEditRides && (
          <button
            onClick={() => openEditDialog(ride)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-sm transition-colors"
            title="Edit ride"
            data-testid={`edit-ride-${ride.id}`}
          >
            <Edit className="h-4 w-4" />
          </button>
          )}
          <button
            onClick={() => {
              setRideToDelete(ride);
              setDeleteDialogOpen(true);
            }}
            className="p-2 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
            title="Delete ride"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  // Function to render mobile ride card
  const renderMobileRideCard = (ride) => (
    <MobileRideCard
      key={ride.id}
      ride={ride}
      formatDateTime={formatDateTime}
      getStatusColor={getStatusColor}
      onView={openDetailModal}
      onEdit={canEditRides ? openEditDialog : null}
      onDuplicate={openDuplicateDialog}
      onDelete={(r) => {
        setRideToDelete(r);
        setDeleteDialogOpen(true);
      }}
      onToggleLive={toggleRideLive}
      onArrivedPickup={markArrivedAtPickup}
      onCustomerPickedUp={markPickedUpCustomer}
      onArrivedDropoff={markArrivedAtDropoff}
      showTrackingButtons={canPressActions(ride)}
    />
  );

  // Function to render tracking buttons
  const renderTrackingButtons = (ride) => (
    <div className="flex flex-col gap-1.5 min-w-[180px]">
      {canPressActions(ride) && !ride.arrived_at_pickup && (
        <Button
          onClick={() => markArrivedAtPickup(ride.id)}
          size="sm"
          className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs"
        >
          <Navigation className="mr-1 h-3 w-3" />
          Arrived Pickup
        </Button>
      )}
      
      {canPressActions(ride) && ride.arrived_at_pickup && !ride.picked_up_customer && (
        <Button
          onClick={() => markPickedUpCustomer(ride.id)}
          size="sm"
          className="h-8 bg-purple-600 hover:bg-purple-700 text-white text-xs"
        >
          <UserCheck className="mr-1 h-3 w-3" />
          Picked Up
        </Button>
      )}
      
      {canPressActions(ride) && ride.picked_up_customer && !ride.arrived_at_dropoff && (
        <Button
          onClick={() => markArrivedAtDropoff(ride.id)}
          size="sm"
          className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
        >
          <Flag className="mr-1 h-3 w-3" />
          Arrived Dropoff
        </Button>
      )}
      
      {ride.arrived_at_dropoff && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center text-emerald-700 text-xs font-medium">
            <CheckCircle2 className="mr-1 h-3 w-3" />
            All Complete
          </div>
        </div>
      )}
      
      {canPressActions(ride) && (ride.arrived_at_pickup || ride.picked_up_customer || ride.arrived_at_dropoff) && (
        <Button
          onClick={() => openResetDialog(ride)}
          size="sm"
          variant="outline"
          className="h-7 border-slate-300 text-slate-600 hover:bg-slate-50 text-xs"
        >
          <RotateCcw className="mr-1 h-3 w-3" />
          Reset
        </Button>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="mb-6 lg:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" data-testid="rides-management-title">Rides Management</h1>
          <p className="text-sm lg:text-base text-slate-600 mt-1 lg:mt-2">Manage all transfer rides and assignments</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-sm shadow-sm"
          onClick={() => setImportDialogOpen(true)}
          data-testid="import-rides-button"
        >
          <FileSpreadsheet className="mr-2 h-5 w-5" />
          Import from Excel
        </Button>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            resetForm();
            // Return to Past Rides if we came from there
            const returnTo = searchParams.get('returnTo');
            if (returnTo === 'past-rides') {
              const returnUrl = new URLSearchParams();
              ['month','year','filterStatus','filterDriver','filterPartner','filterVehicle','searchTerm','page','filterEmptyField'].forEach(key => {
                const val = searchParams.get(key);
                if (val) returnUrl.set(key, val);
              });
              const qs = returnUrl.toString();
              navigate(`/admin/past-rides${qs ? '?' + qs : ''}`);
            }
          }
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm shadow-sm"
              data-testid="create-ride-button"
            >
              <Plus className="mr-2 h-5 w-5" />
              Create Ride
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRide ? 'Edit Ride' : 'Create New Ride'}</DialogTitle>
              <DialogDescription>
                {editingRide ? 'Update ride details and assignments' : 'Enter ride details and assign a driver'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4" data-testid="ride-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="customer_name" className="text-slate-700 font-medium mb-2 block">
                    Customer Name
                  </Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    placeholder="John Doe"
                    className="h-11 rounded-sm"
                    data-testid="customer-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="customer_phone" className="text-slate-700 font-medium mb-2 block">
                    Customer Phone
                  </Label>
                  <Input
                    id="customer_phone"
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    className="h-11 rounded-sm"
                    data-testid="customer-phone-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="number_of_people" className="text-slate-700 font-medium mb-2 block">
                    Number of People
                  </Label>
                  <Input
                    id="number_of_people"
                    type="number"
                    min="1"
                    value={formData.number_of_people}
                    onChange={(e) => setFormData({ ...formData, number_of_people: e.target.value })}
                    placeholder="e.g. 2"
                    className="h-11 rounded-sm"
                    data-testid="number-of-people-input"
                  />
                </div>

                <div>
                  <Label htmlFor="flight_number" className="text-slate-700 font-medium mb-2 block">
                    Flight Number
                  </Label>
                  <Input
                    id="flight_number"
                    type="text"
                    value={formData.flight_number}
                    onChange={(e) => setFormData({ ...formData, flight_number: e.target.value })}
                    placeholder="e.g. AA1234"
                    className="h-11 rounded-sm"
                    data-testid="flight-number-input"
                  />
                  {/* Airport Pickup Toggle */}
                  <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="airport-pickup-toggle">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, is_airport_pickup: !prev.is_airport_pickup }))}
                      className={`relative w-10 h-5 rounded-full transition-colors ${formData.is_airport_pickup ? 'bg-blue-500' : 'bg-slate-300'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${formData.is_airport_pickup ? 'translate-x-5' : ''}`} />
                    </button>
                    <span className="text-sm text-slate-600">Airport Pickup <span className="text-xs text-slate-400">(30 min tolerance)</span></span>
                  </label>
                </div>

                <div>
                  <Label htmlFor="booking_number" className="text-slate-700 font-medium mb-2 block">
                    Booking Number
                  </Label>
                  <Input
                    id="booking_number"
                    type="text"
                    value={formData.booking_number}
                    onChange={(e) => setFormData({ ...formData, booking_number: e.target.value })}
                    placeholder="e.g. BK-12345"
                    className="h-11 rounded-sm"
                    data-testid="booking-number-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="date_time" className="text-slate-700 font-medium mb-2 block">
                    Date & Time
                  </Label>
                  <Input
                    id="date_time"
                    type="datetime-local"
                    value={formData.date_time}
                    onChange={(e) => setFormData({ ...formData, date_time: e.target.value })}
                    className="h-11 rounded-sm"
                    data-testid="date-time-input"
                    step="60"
                  />
                </div>

                <div></div>
              </div>

              <div>
                <Label htmlFor="pickup_location" className="text-slate-700 font-medium mb-2 block">
                  Pickup Location
                </Label>
                <div className="flex gap-2">
                  <Select
                    onValueChange={(value) => {
                      if (value !== 'custom') {
                        handlePickupLocationChange(value);
                      }
                    }}
                  >
                    <SelectTrigger className="w-[200px] h-11 rounded-sm">
                      <SelectValue placeholder="Quick select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ATHENS AIRPORT">Athens Airport</SelectItem>
                      <SelectItem value="PIRAEUS PORT">Piraeus Port</SelectItem>
                      <SelectItem value="custom">Custom Address...</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex-1 relative">
                    <Input
                      id="pickup_location"
                      value={formData.pickup_location}
                      onChange={(e) => handlePickupLocationChange(e.target.value)}
                      placeholder="Or type custom address..."
                      className="h-11 rounded-sm pr-8"
                      data-testid="pickup-location-input"
                    />
                    {geocodingPickup && (
                      <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-blue-500" />
                    )}
                  </div>
                </div>
                {/* Show detected location for verification */}
                {detectedPickupLocation && (
                  <div className="mt-1 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                    <span className="font-medium text-blue-700">Detected: </span>
                    <span className="text-blue-600">{detectedPickupLocation}</span>
                    <p className="text-blue-500 mt-1">Please verify this is the correct location. If not, try adding more details (city, country).</p>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="dropoff_location" className="text-slate-700 font-medium mb-2 block">
                  Drop-off Location
                </Label>
                <div className="flex gap-2">
                  <Select
                    onValueChange={(value) => {
                      if (value !== 'custom') {
                        handleDropoffLocationChange(value);
                      }
                    }}
                  >
                    <SelectTrigger className="w-[200px] h-11 rounded-sm">
                      <SelectValue placeholder="Quick select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ATHENS AIRPORT">Athens Airport</SelectItem>
                      <SelectItem value="PIRAEUS PORT">Piraeus Port</SelectItem>
                      <SelectItem value="custom">Custom Address...</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex-1 relative">
                    <Input
                      id="dropoff_location"
                      value={formData.dropoff_location}
                      onChange={(e) => handleDropoffLocationChange(e.target.value)}
                      placeholder="Or type custom address..."
                      className="h-11 rounded-sm pr-8"
                      data-testid="dropoff-location-input"
                    />
                    {geocodingDropoff && (
                      <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-blue-500" />
                    )}
                  </div>
                </div>
                {/* Show detected location for verification */}
                {detectedDropoffLocation && (
                  <div className="mt-1 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                    <span className="font-medium text-blue-700">Detected: </span>
                    <span className="text-blue-600">{detectedDropoffLocation}</span>
                    <p className="text-blue-500 mt-1">Please verify this is the correct location. If not, try adding more details (city, country).</p>
                  </div>
                )}
              </div>

              {/* GPS Coordinates Section */}
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4 text-slate-500" />
                  <Label className="text-slate-700 font-medium">GPS Coordinates (for Geofencing)</Label>
                  {(geocodingPickup || geocodingDropoff) ? (
                    <span className="text-xs text-blue-500 ml-auto flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Auto-detecting coordinates...
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400 ml-auto">Auto-filled from addresses</span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="pickup_lat" className="text-xs text-slate-500 mb-1 block">Pickup Lat</Label>
                    <Input
                      id="pickup_lat"
                      type="number"
                      step="0.000001"
                      value={formData.pickup_lat}
                      onChange={(e) => setFormData({ ...formData, pickup_lat: e.target.value })}
                      placeholder="37.9838"
                      className={`h-9 rounded-sm text-sm ${formData.pickup_lat ? 'bg-green-50 border-green-200' : ''}`}
                      data-testid="pickup-lat-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pickup_lng" className="text-xs text-slate-500 mb-1 block">Pickup Lng</Label>
                    <Input
                      id="pickup_lng"
                      type="number"
                      step="0.000001"
                      value={formData.pickup_lng}
                      onChange={(e) => setFormData({ ...formData, pickup_lng: e.target.value })}
                      placeholder="23.7275"
                      className={`h-9 rounded-sm text-sm ${formData.pickup_lng ? 'bg-green-50 border-green-200' : ''}`}
                      data-testid="pickup-lng-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dropoff_lat" className="text-xs text-slate-500 mb-1 block">Dropoff Lat</Label>
                    <Input
                      id="dropoff_lat"
                      type="number"
                      step="0.000001"
                      value={formData.dropoff_lat}
                      onChange={(e) => setFormData({ ...formData, dropoff_lat: e.target.value })}
                      placeholder="37.9364"
                      className={`h-9 rounded-sm text-sm ${formData.dropoff_lat ? 'bg-green-50 border-green-200' : ''}`}
                      data-testid="dropoff-lat-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dropoff_lng" className="text-xs text-slate-500 mb-1 block">Dropoff Lng</Label>
                    <Input
                      id="dropoff_lng"
                      type="number"
                      step="0.000001"
                      value={formData.dropoff_lng}
                      onChange={(e) => setFormData({ ...formData, dropoff_lng: e.target.value })}
                      placeholder="23.9445"
                      className={`h-9 rounded-sm text-sm ${formData.dropoff_lng ? 'bg-green-50 border-green-200' : ''}`}
                      data-testid="dropoff-lng-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="vehicle_type" className="text-slate-700 font-medium block">
                      Vehicle Type
                    </Label>
                    <button
                      type="button"
                      onClick={openManageVehicleTypes}
                      className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                      data-testid="manage-vehicle-types-btn"
                    >
                      Manage
                    </button>
                  </div>
                  <Select
                    value={formData.vehicle_type}
                    onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                  >
                    <SelectTrigger className="h-11 rounded-sm" data-testid="vehicle-type-select">
                      <SelectValue placeholder="Select vehicle type" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes.map((vt) => (
                        <SelectItem key={vt} value={vt}>{vt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="price" className="text-slate-700 font-medium mb-2 block">
                    Price (€)
                  </Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="50.00"
                    className="h-11 rounded-sm"
                    data-testid="price-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Customer price</p>
                </div>

                <div>
                  <Label htmlFor="payment_to_partner" className="text-slate-700 font-medium mb-2 block">
                    Payment to Partner (€)
                  </Label>
                  <Input
                    id="payment_to_partner"
                    type="number"
                    step="0.01"
                    value={formData.payment_to_partner}
                    onChange={(e) => setFormData({ ...formData, payment_to_partner: e.target.value })}
                    placeholder="40.00"
                    className="h-11 rounded-sm"
                    data-testid="payment-to-partner-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Amount to pay partner</p>
                </div>

                <div>
                  <Label htmlFor="driver_commission" className="text-slate-700 font-medium mb-2 block">
                    Driver Commission (€)
                  </Label>
                  <Input
                    id="driver_commission"
                    type="number"
                    step="0.01"
                    value={formData.driver_commission}
                    onChange={(e) => setFormData({ ...formData, driver_commission: e.target.value })}
                    placeholder="15.00"
                    className="h-11 rounded-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="commission_driver_id" className="text-slate-700 font-medium mb-2 block">
                    Commission To (optional)
                  </Label>
                  <Select
                    value={formData.commission_driver_id || 'same_as_assigned'}
                    onValueChange={(value) => setFormData({ ...formData, commission_driver_id: value === 'same_as_assigned' ? '' : value })}
                  >
                    <SelectTrigger className="h-11 rounded-sm" data-testid="commission-driver-select">
                      <SelectValue placeholder="Same as assigned driver" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="same_as_assigned">Same as assigned driver</SelectItem>
                      {drivers.map((driver) => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Assign commission to a different driver</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="assigned_driver_id" className="text-slate-700 font-medium mb-2 block">
                    Assign Driver
                  </Label>
                  <Select
                    value={formData.assigned_driver_id}
                    onValueChange={(value) => {
                      const newFormData = { ...formData, assigned_driver_id: value };
                      // Auto-select the driver's vehicle if driver has one
                      if (value !== 'unassigned') {
                        const selectedDriver = drivers.find(d => d.id === value);
                        if (selectedDriver && selectedDriver.vehicle_id) {
                          newFormData.vehicle_id = selectedDriver.vehicle_id;
                        }
                      }
                      setFormData(newFormData);
                    }}
                  >
                    <SelectTrigger className="h-11 rounded-sm" data-testid="driver-select">
                      <SelectValue placeholder="Select a driver (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {drivers.map((driver) => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.name} ({driver.email}) {driver.vehicle_id ? '🚗' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="vehicle_id" className="text-slate-700 font-medium mb-2 block">
                    Assign Vehicle
                  </Label>
                  <Select
                    value={formData.vehicle_id || '_none'}
                    onValueChange={(value) => setFormData({ ...formData, vehicle_id: value === '_none' ? '' : value })}
                  >
                    <SelectTrigger className="h-11 rounded-sm" data-testid="vehicle-select">
                      <SelectValue placeholder="Select a vehicle (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No Vehicle</SelectItem>
                      {vehicles.filter(v => v.is_active !== false).map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.plate_number} {vehicle.model ? `(${vehicle.model})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="status" className="text-slate-700 font-medium mb-2 block">
                    Status
                  </Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="h-11 rounded-sm" data-testid="status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="no_show">No Show</SelectItem>
                      <SelectItem value="driver_no_show">Driver No-Show</SelectItem>
                      <SelectItem value="paid_cancelled">Paid Cancelled</SelectItem>
                      <SelectItem value="free_cancelled">Free Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="partner" className="text-slate-700 font-medium mb-2 block">
                  Partner
                </Label>
                <div className="relative" ref={partnerDropdownRef}>
                  <div className="relative">
                    <Input
                      id="partner"
                      value={formData.partner}
                      onChange={(e) => {
                        setFormData({ ...formData, partner: e.target.value });
                        setPartnerSearch(e.target.value);
                        setPartnerDropdownOpen(true);
                      }}
                      onFocus={() => setPartnerDropdownOpen(true)}
                      placeholder="Select or type a new partner..."
                      className="h-11 rounded-sm pr-10"
                      data-testid="partner-input"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => setPartnerDropdownOpen(!partnerDropdownOpen)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded"
                    >
                      <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${partnerDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                  
                  {/* Partner Dropdown */}
                  {partnerDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
                      {/* Show filtered existing partners */}
                      {uniquePartners
                        .filter(p => !formData.partner || p.toLowerCase().includes(formData.partner.toLowerCase()))
                        .map((partner) => (
                          <button
                            key={partner}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, partner });
                              setPartnerDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 hover:bg-slate-100 text-sm ${
                              formData.partner === partner ? 'bg-slate-50 font-medium' : ''
                            }`}
                          >
                            {partner}
                          </button>
                        ))}
                      
                      {/* Show option to add new partner if typed value doesn't exist */}
                      {formData.partner && 
                       !uniquePartners.some(p => p.toLowerCase() === formData.partner.toLowerCase()) && (
                        <button
                          type="button"
                          onClick={() => {
                            setPartnerDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-emerald-50 text-sm text-emerald-700 border-t border-slate-100 flex items-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add "{formData.partner}" as new partner
                        </button>
                      )}
                      
                      {/* Empty state */}
                      {uniquePartners.length === 0 && !formData.partner && (
                        <div className="px-4 py-3 text-sm text-slate-500 text-center">
                          No partners yet. Type to add your first partner.
                        </div>
                      )}
                      
                      {/* No matches state */}
                      {uniquePartners.length > 0 && 
                       formData.partner && 
                       !uniquePartners.some(p => p.toLowerCase().includes(formData.partner.toLowerCase())) && 
                       !uniquePartners.some(p => p.toLowerCase() === formData.partner.toLowerCase()) && (
                        <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">
                          No matching partners found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Select from list or type a new partner name
                </p>
              </div>

              {/* POS Taxable Toggle — only meaningful when partner == POS */}
              {formData.partner && formData.partner.toUpperCase() === 'POS' && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-start gap-3" data-testid="pos-taxable-row">
                  <Checkbox
                    id="pos_taxable"
                    checked={formData.pos_taxable !== false}
                    onCheckedChange={(checked) => setFormData({ ...formData, pos_taxable: !!checked })}
                    className="mt-0.5"
                    data-testid="pos-taxable-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="pos_taxable" className="text-sm font-medium text-amber-900 cursor-pointer">
                      Apply 24% tax (Savings) on this POS payment
                    </Label>
                    <p className="text-xs text-amber-800 mt-1">
                      When enabled, the auto-created Income entry will be flagged taxable and a linked
                      Savings entry will be created. Disable for non-taxable POS rides.
                    </p>
                  </div>
                </div>
              )}

              {/* Partner Account (for Partner Portal access) */}
              {registeredPartners.length > 0 && (
                <div>
                  <Label htmlFor="partner_id" className="text-slate-700 font-medium mb-2 block">
                    Partner Account (Portal Access)
                  </Label>
                  <Select
                    value={formData.partner_id || 'none'}
                    onValueChange={(value) => setFormData({ ...formData, partner_id: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger className="h-11 rounded-sm" data-testid="partner-account-select">
                      <SelectValue placeholder="Select partner account..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No partner account</SelectItem>
                      {registeredPartners.filter(p => p.is_active).map(partner => (
                        <SelectItem key={partner.id} value={partner.id}>
                          {partner.name} {partner.company ? `(${partner.company})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Link this ride to a registered partner for their portal access
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="notes" className="text-slate-700 font-medium mb-2 block">
                  Notes
                </Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes or special instructions..."
                  rows={3}
                  className="rounded-sm"
                  data-testid="notes-input"
                />
              </div>

              {/* Extras Section */}
              <div className="border-t border-slate-200 pt-4">
                <Label className="text-slate-700 font-medium mb-3 block">
                  Extra
                </Label>
                {/* Selected extras */}
                {formData.extras && formData.extras.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.extras.map((extra) => (
                      <span
                        key={extra}
                        className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm"
                        data-testid={`extra-tag-${extra}`}
                      >
                        {extra}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, extras: prev.extras.filter(v => v !== extra) }))}
                          className="ml-1 text-purple-500 hover:text-purple-700"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {/* Add from catalog */}
                {extrasCatalog.filter(e => !(formData.extras || []).includes(e)).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {extrasCatalog.filter(e => !(formData.extras || []).includes(e)).map((extra) => (
                      <button
                        key={extra}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, extras: [...(prev.extras || []), extra] }))}
                        className="text-xs bg-slate-100 hover:bg-purple-100 text-slate-600 hover:text-purple-700 px-2.5 py-1 rounded-full transition-colors border border-slate-200 hover:border-purple-300"
                        data-testid={`catalog-extra-${extra}`}
                      >
                        + {extra}
                      </button>
                    ))}
                  </div>
                )}
                {/* Add new custom extra */}
                <div className="flex gap-2">
                  <Input
                    value={newExtraInput}
                    onChange={(e) => setNewExtraInput(e.target.value)}
                    placeholder="Add new extra..."
                    className="h-9 rounded-sm text-sm flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newExtraInput.trim()) {
                        e.preventDefault();
                        const name = newExtraInput.trim().toUpperCase();
                        if (!(formData.extras || []).includes(name)) {
                          setFormData(prev => ({ ...prev, extras: [...(prev.extras || []), name] }));
                        }
                        setNewExtraInput('');
                      }
                    }}
                    data-testid="new-extra-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (newExtraInput.trim()) {
                        const name = newExtraInput.trim().toUpperCase();
                        if (!(formData.extras || []).includes(name)) {
                          setFormData(prev => ({ ...prev, extras: [...(prev.extras || []), name] }));
                        }
                        setNewExtraInput('');
                      }
                    }}
                    className="h-9 px-3"
                    data-testid="add-extra-btn"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Voucher Section - Only show when editing existing ride */}
              {editingRide && (
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Vouchers
                    </Label>
                    <input
                      ref={voucherInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleVoucherUpload(e.target.files[0]);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => voucherInputRef.current?.click()}
                      disabled={uploadingVoucher}
                      className="gap-2"
                      data-testid="upload-voucher-btn"
                    >
                      <Upload className="h-4 w-4" />
                      {uploadingVoucher ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                  
                  {editingRideVouchers.length > 0 ? (
                    <div className="space-y-2">
                      {editingRideVouchers.map((voucher) => (
                        <div
                          key={voucher.id}
                          className="flex items-center justify-between bg-slate-50 p-3 rounded-sm border border-slate-200"
                          data-testid={`voucher-${voucher.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <File className="h-5 w-5 text-slate-500" />
                            <div>
                              <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                                {voucher.filename}
                              </p>
                              <p className="text-xs text-slate-500">
                                Uploaded by {voucher.uploaded_by}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                downloadVoucherWithAuth(editingRide.id, voucher.id, voucher.filename, token)
                                  .then(() => toast.success('Voucher downloaded successfully'))
                                  .catch(() => toast.error('Failed to download voucher'));
                              }}
                              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-sm"
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleVoucherDelete(voucher.id)}
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">
                      No vouchers uploaded. Click "Upload" to add voucher files.
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-2">
                    Accepted formats: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX
                  </p>
                </div>
              )}

              {/* Port Files Section - Only show when editing existing ride */}
              {editingRide && (
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4 text-cyan-600" />
                      Port
                    </Label>
                    <input
                      ref={portFileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handlePortFileUpload(e.target.files[0]);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => portFileInputRef.current?.click()}
                      disabled={uploadingPortFile}
                      className="gap-2"
                      data-testid="upload-port-file-btn"
                    >
                      <Upload className="h-4 w-4" />
                      {uploadingPortFile ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                  
                  {editingRidePortFiles.length > 0 ? (
                    <div className="space-y-2">
                      {editingRidePortFiles.map((portFile) => (
                        <div
                          key={portFile.id}
                          className="flex items-center justify-between bg-cyan-50 p-3 rounded-sm border border-cyan-200"
                          data-testid={`port-file-${portFile.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <File className="h-5 w-5 text-cyan-600" />
                            <div>
                              <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                                {portFile.filename}
                              </p>
                              <p className="text-xs text-slate-500">
                                Uploaded by {portFile.uploaded_by}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                downloadPortFileWithAuth(editingRide.id, portFile.id, portFile.filename, token)
                                  .then(() => toast.success('Port file downloaded successfully'))
                                  .catch(() => toast.error('Failed to download port file'));
                              }}
                              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-sm"
                              title="Download"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePortFileDelete(portFile.id)}
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">
                      No port files uploaded. Click "Upload" to add port files.
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-2">
                    Accepted formats: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11"
                  data-testid="submit-ride-button"
                >
                  {editingRide ? 'Update Ride' : 'Create Ride'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                    // Return to Past Rides if we came from there
                    const returnTo = searchParams.get('returnTo');
                    if (returnTo === 'past-rides') {
                      const returnUrl = new URLSearchParams();
                      const month = searchParams.get('month');
                      const year = searchParams.get('year');
                      const filterStatus = searchParams.get('filterStatus');
                      const filterDriver = searchParams.get('filterDriver');
                      const filterPartner = searchParams.get('filterPartner');
                      const filterVehicle = searchParams.get('filterVehicle');
                      const searchTerm = searchParams.get('searchTerm');
                      const page = searchParams.get('page');
                      const filterEmptyFieldParam = searchParams.get('filterEmptyField');
                      if (month) returnUrl.set('month', month);
                      if (year) returnUrl.set('year', year);
                      if (filterStatus) returnUrl.set('filterStatus', filterStatus);
                      if (filterDriver) returnUrl.set('filterDriver', filterDriver);
                      if (filterPartner) returnUrl.set('filterPartner', filterPartner);
                      if (filterVehicle) returnUrl.set('filterVehicle', filterVehicle);
                      if (searchTerm) returnUrl.set('searchTerm', searchTerm);
                      if (page) returnUrl.set('page', page);
                      if (filterEmptyFieldParam) returnUrl.set('filterEmptyField', filterEmptyFieldParam);
                      const qs = returnUrl.toString();
                      navigate(`/admin/past-rides${qs ? '?' + qs : ''}`);
                    }
                  }}
                  className="flex-1 rounded-sm h-11"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <RidesImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        partners={registeredPartners}
        partnerNames={uniquePartners}
        onImported={() => fetchData()}
      />

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm mb-6 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Filter className="h-5 w-5 text-slate-500" />
            <h3 className="font-semibold text-slate-900">Filters</h3>
            {(filterStatus !== 'all' || filterDriver !== 'all' || searchTerm || hasActiveAdvancedFilters) && (
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                {filteredRides.length} of {rides.length} rides
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {/* Include Cancelled Toggle */}
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg" data-testid="include-cancelled-toggle">
              <Switch
                id="include-cancelled"
                checked={includeCancelled}
                onCheckedChange={setIncludeCancelled}
              />
              <Label htmlFor="include-cancelled" className="text-sm text-slate-600 cursor-pointer">
                Include cancelled
              </Label>
              {includeCancelled && (
                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Active</span>
              )}
            </div>
            {(filterStatus !== 'all' || filterDriver !== 'all' || searchTerm || hasActiveAdvancedFilters) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-slate-500 hover:text-slate-700"
              >
                Clear All
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={showAdvancedFilters ? 'bg-slate-100' : ''}
            >
              {showAdvancedFilters ? 'Hide Advanced' : 'Advanced Filters'}
            </Button>
          </div>
        </div>

        {/* Basic Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Search All Fields</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Customer, phone, location, flight, notes..."
                className="pl-10 h-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-10 rounded-sm" data-testid="filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="no_show">No Show</SelectItem>
                <SelectItem value="driver_no_show">Driver No-Show</SelectItem>
                <SelectItem value="paid_cancelled">Paid Cancelled</SelectItem>
                <SelectItem value="free_cancelled">Free Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Driver</Label>
            <Select value={filterDriver} onValueChange={setFilterDriver}>
              <SelectTrigger className="h-10 rounded-sm" data-testid="filter-driver">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drivers</SelectItem>
                {drivers.map((driver) => (
                  <SelectItem key={driver.id} value={driver.id}>
                    {driver.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-700 text-sm mb-2 block">Partner</Label>
            <Select value={filterPartner} onValueChange={setFilterPartner}>
              <SelectTrigger className="h-10 rounded-sm" data-testid="filter-partner">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partners</SelectItem>
                {uniquePartners.map((partner) => (
                  <SelectItem key={partner} value={partner}>
                    {partner}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Advanced Filters - Collapsible */}
        {showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label className="text-slate-700 text-sm mb-2 block">Vehicle Type</Label>
                <Select value={filterVehicle} onValueChange={setFilterVehicle}>
                  <SelectTrigger className="h-10 rounded-sm" data-testid="filter-vehicle">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Vehicles</SelectItem>
                    {uniqueVehicles.map((vehicle) => (
                      <SelectItem key={vehicle} value={vehicle}>
                        {vehicle}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700 text-sm mb-2 block">Number Plate</Label>
                <Select value={filterPlate} onValueChange={setFilterPlate}>
                  <SelectTrigger className="h-10 rounded-sm" data-testid="filter-plate">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plates</SelectItem>
                    {uniquePlates.map((plate) => (
                      <SelectItem key={plate} value={plate}>
                        {plate}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700 text-sm mb-2 block">Date From</Label>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="h-10 rounded-sm"
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <Label className="text-slate-700 text-sm mb-2 block">Date To</Label>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="h-10 rounded-sm"
                  data-testid="filter-date-to"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-slate-700 text-sm mb-2 block">Min Price (€)</Label>
                  <Input
                    type="number"
                    value={filterPriceMin}
                    onChange={(e) => setFilterPriceMin(e.target.value)}
                    placeholder="0"
                    className="h-10 rounded-sm"
                    data-testid="filter-price-min"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm mb-2 block">Max Price (€)</Label>
                  <Input
                    type="number"
                    value={filterPriceMax}
                    onChange={(e) => setFilterPriceMax(e.target.value)}
                    placeholder="∞"
                    className="h-10 rounded-sm"
                    data-testid="filter-price-max"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 text-sm mb-2 block">Empty Field</Label>
                <Select value={filterEmptyField} onValueChange={setFilterEmptyField}>
                  <SelectTrigger className="h-10 rounded-sm" data-testid="filter-empty-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Filter</SelectItem>
                    <SelectItem value="booking_number">Booking Number</SelectItem>
                    <SelectItem value="customer_name">Customer Name</SelectItem>
                    <SelectItem value="customer_phone">Phone</SelectItem>
                    <SelectItem value="flight_number">Flight Number</SelectItem>
                    <SelectItem value="assigned_driver_id">Driver</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="price">Price</SelectItem>
                    <SelectItem value="payment_to_partner">Partner Price</SelectItem>
                    <SelectItem value="pickup_location">Pickup Location</SelectItem>
                    <SelectItem value="dropoff_location">Dropoff Location</SelectItem>
                    <SelectItem value="vehicle_type">Vehicle Type</SelectItem>
                    <SelectItem value="notes">Notes</SelectItem>
                    <SelectItem value="payment_method">Payment Method</SelectItem>
                    <SelectItem value="number_of_people">Passengers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Tags */}
        {(filterStatus !== 'all' || filterDriver !== 'all' || filterPartner !== 'all' || 
          filterVehicle !== 'all' || filterPlate !== 'all' || filterDateFrom || filterDateTo || filterPriceMin || filterPriceMax || filterEmptyField !== 'none') && (
          <div className="mt-4 flex flex-wrap gap-2">
            {filterStatus !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-sky-100 text-sky-800 rounded-full text-xs">
                Status: {filterStatus.replace('_', ' ')}
                <button onClick={() => setFilterStatus('all')} className="hover:bg-sky-200 rounded-full p-0.5">×</button>
              </span>
            )}
            {filterDriver !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                Driver: {drivers.find(d => d.id === filterDriver)?.name || filterDriver}
                <button onClick={() => setFilterDriver('all')} className="hover:bg-purple-200 rounded-full p-0.5">×</button>
              </span>
            )}
            {filterPartner !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs">
                Partner: {filterPartner}
                <button onClick={() => setFilterPartner('all')} className="hover:bg-amber-200 rounded-full p-0.5">×</button>
              </span>
            )}
            {filterVehicle !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                Vehicle: {filterVehicle}
                <button onClick={() => setFilterVehicle('all')} className="hover:bg-green-200 rounded-full p-0.5">×</button>
              </span>
            )}
            {filterPlate !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-800 rounded-full text-xs">
                Plate: {filterPlate}
                <button onClick={() => setFilterPlate('all')} className="hover:bg-teal-200 rounded-full p-0.5">×</button>
              </span>
            )}
            {filterDateFrom && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs">
                From: {filterDateFrom}
                <button onClick={() => setFilterDateFrom('')} className="hover:bg-indigo-200 rounded-full p-0.5">×</button>
              </span>
            )}
            {filterDateTo && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs">
                To: {filterDateTo}
                <button onClick={() => setFilterDateTo('')} className="hover:bg-indigo-200 rounded-full p-0.5">×</button>
              </span>
            )}
            {filterPriceMin && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs">
                Min €{filterPriceMin}
                <button onClick={() => setFilterPriceMin('')} className="hover:bg-emerald-200 rounded-full p-0.5">×</button>
              </span>
            )}
            {filterPriceMax && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs">
                Max €{filterPriceMax}
                <button onClick={() => setFilterPriceMax('')} className="hover:bg-emerald-200 rounded-full p-0.5">×</button>
              </span>
            )}
            {filterEmptyField !== 'none' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                Empty: {filterEmptyField.replace('_', ' ')}
                <button onClick={() => setFilterEmptyField('none')} className="hover:bg-red-200 rounded-full p-0.5">×</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* TAB NAVIGATION */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm mb-6">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('today')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
              activeTab === 'today'
                ? 'text-emerald-700 bg-emerald-50'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
            data-testid="tab-today"
          >
            <div className="flex items-center justify-center gap-2">
              <Clock className="h-4 w-4" />
              <span>Today&apos;s Rides</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                activeTab === 'today' ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-200 text-slate-700'
              }`}>
                {todayRides.length}
              </span>
            </div>
            {activeTab === 'today' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600"></div>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('tomorrow')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
              activeTab === 'tomorrow'
                ? 'text-purple-700 bg-purple-50'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
            data-testid="tab-tomorrow"
          >
            <div className="flex items-center justify-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <span>Tomorrow&apos;s Rides</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                activeTab === 'tomorrow' ? 'bg-purple-200 text-purple-800' : 'bg-slate-200 text-slate-700'
              }`}>
                {tomorrowRides.length}
              </span>
            </div>
            {activeTab === 'tomorrow' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600"></div>
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('future')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${
              activeTab === 'future'
                ? 'text-blue-700 bg-blue-50'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
            data-testid="tab-future"
          >
            <div className="flex items-center justify-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <span>Future Rides</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                activeTab === 'future' ? 'bg-blue-200 text-blue-800' : 'bg-slate-200 text-slate-700'
              }`}>
                {futureRides.length}
              </span>
            </div>
            {activeTab === 'future' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
            )}
          </button>
        </div>
        
        {/* Unscheduled Rides Notice */}
        {unscheduledRides.length > 0 && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            <span><strong>{unscheduledRides.length}</strong> unscheduled ride(s) - shown in all tabs</span>
          </div>
        )}
      </div>

      {/* TAB CONTENT - Today's Rides */}
      {activeTab === 'today' && (
        <>
        {activeNowRides.length > 0 && (
          <div className="bg-white border border-sky-200 rounded-xl lg:rounded-sm shadow-sm overflow-hidden mb-4" data-testid="active-rides-panel">
            <div className="px-4 lg:px-6 py-3 lg:py-4 bg-sky-50 border-b border-sky-200">
              <div className="flex items-center gap-2">
                <Radio className="h-5 w-5 text-sky-600 animate-pulse" />
                <h3 className="font-semibold text-sky-900">Active Rides ({activeNowRides.length})</h3>
              </div>
            </div>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full" data-testid="rides-table-active">
                <thead>
                  <tr>
                    <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[0].left, minWidth: stickyStyles[0].minWidth }}>Customer</th>
                    <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[1].left, minWidth: stickyStyles[1].minWidth }}>Date & Time</th>
                    <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[2].left, minWidth: stickyStyles[2].minWidth }}>Route</th>
                    <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[3].left, minWidth: stickyStyles[3].minWidth }}>Driver</th>
                    <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[4].left, minWidth: stickyStyles[4].minWidth, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.08)' }}>Partner</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Vehicle</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">V. Type</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Extra</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Price</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Commission</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Tracking</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeNowRides.map(renderRideRow)}
                </tbody>
              </table>
            </div>
            {/* Mobile Card View */}
            <div className="lg:hidden p-4 space-y-4">
              {activeNowRides.map(renderMobileRideCard)}
            </div>
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-xl lg:rounded-sm shadow-sm overflow-hidden">
          <div className="px-4 lg:px-6 py-3 lg:py-4 bg-emerald-50 border-b border-emerald-200">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-emerald-600" />
              <h3 className="font-semibold text-emerald-900">Today&apos;s Rides ({todayQueueRides.length})</h3>
            </div>
          </div>
          
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full" data-testid="rides-table-today">
              <thead>
                <tr>
                  <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[0].left, minWidth: stickyStyles[0].minWidth }}>Customer</th>
                  <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[1].left, minWidth: stickyStyles[1].minWidth }}>Date & Time</th>
                  <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[2].left, minWidth: stickyStyles[2].minWidth }}>Route</th>
                  <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[3].left, minWidth: stickyStyles[3].minWidth }}>Driver</th>
                  <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[4].left, minWidth: stickyStyles[4].minWidth, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.08)' }}>Partner</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Vehicle</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">V. Type</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Extra</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Price</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Commission</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Tracking</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Actions</th>
                </tr>
              </thead>
              <tbody>
                {todayQueueRides.length === 0 && unscheduledRides.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="px-6 py-12 text-center text-slate-500">
                      No rides scheduled for today
                    </td>
                  </tr>
                ) : (
                  <>
                    {todayQueueRides.map(renderRideRow)}
                    {unscheduledRides.length > 0 && (
                      <>
                        <tr className="bg-amber-50 border-t-2 border-amber-200">
                          <td colSpan="11" className="px-6 py-3">
                            <div className="flex items-center gap-2">
                              <CalendarDays className="h-5 w-5 text-amber-600" />
                              <span className="text-sm font-bold text-amber-900 uppercase tracking-wide">
                                Unscheduled Rides ({unscheduledRides.length})
                              </span>
                            </div>
                          </td>
                        </tr>
                        {unscheduledRides.map(renderRideRow)}
                      </>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden p-4 space-y-4">
            {todayQueueRides.length === 0 && unscheduledRides.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                No rides scheduled for today
              </div>
            ) : (
              <>
                {todayQueueRides.map(renderMobileRideCard)}
                {unscheduledRides.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 py-3 px-2 bg-amber-50 rounded-lg">
                      <CalendarDays className="h-5 w-5 text-amber-600" />
                      <span className="text-sm font-bold text-amber-900">
                        Unscheduled Rides ({unscheduledRides.length})
                      </span>
                    </div>
                    {unscheduledRides.map(renderMobileRideCard)}
                  </>
                )}
              </>
            )}
          </div>
        </div>
        </>
      )}

      {/* TAB CONTENT - Tomorrow's Rides */}
      {activeTab === 'tomorrow' && (
        <div className="bg-white border border-slate-200 rounded-xl lg:rounded-sm shadow-sm overflow-hidden">
          <div className="px-4 lg:px-6 py-3 lg:py-4 bg-purple-50 border-b border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-purple-600" />
                <h3 className="font-semibold text-purple-900">Tomorrow&apos;s Rides ({tomorrowRides.length})</h3>
                {/* Show count of non-live rides */}
                {tomorrowRides.filter(r => !r.is_live).length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
                    {tomorrowRides.filter(r => !r.is_live).length} not live
                  </span>
                )}
              </div>
              {/* Set All LIVE button */}
              {tomorrowRides.filter(r => !r.is_live).length > 0 && (
                <Button
                  onClick={setAllTomorrowRidesLive}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="set-all-tomorrow-live"
                >
                  <Zap className="h-4 w-4 mr-1" />
                  Set All LIVE
                </Button>
              )}
              {/* Set All OFFLINE button */}
              {tomorrowRides.filter(r => r.is_live).length > 0 && (
                <Button
                  onClick={setAllTomorrowRidesOffline}
                  size="sm"
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100"
                  data-testid="set-all-tomorrow-offline"
                >
                  <ZapOff className="h-4 w-4 mr-1" />
                  Set All OFFLINE
                </Button>
              )}
            </div>
          </div>
          
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full" data-testid="rides-table-tomorrow">
              <thead>
                <tr>
                  <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[0].left, minWidth: stickyStyles[0].minWidth }}>Customer</th>
                  <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[1].left, minWidth: stickyStyles[1].minWidth }}>Date & Time</th>
                  <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[2].left, minWidth: stickyStyles[2].minWidth }}>Route</th>
                  <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[3].left, minWidth: stickyStyles[3].minWidth }}>Driver</th>
                  <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[4].left, minWidth: stickyStyles[4].minWidth, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.08)' }}>Partner</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Vehicle</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">V. Type</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Extra</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Price</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Commission</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Tracking</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tomorrowRides.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="px-6 py-12 text-center text-slate-500">
                      No rides scheduled for tomorrow
                    </td>
                  </tr>
                ) : (
                  tomorrowRides.map(renderRideRow)
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden p-4 space-y-4">
            {tomorrowRides.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                No rides scheduled for tomorrow
              </div>
            ) : (
              tomorrowRides.map(renderMobileRideCard)
            )}
          </div>
        </div>
      )}

      {/* TAB CONTENT - Future Rides */}
      {activeTab === 'future' && (
        <div>
          {/* Month/Year Navigation for Future Rides */}
          <div className="bg-slate-100 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={goToPreviousFutureMonth}
                className="h-12 w-12 bg-white rounded-lg border-slate-200"
                data-testid="future-prev-month-btn"
              >
                <ChevronLeft className="h-5 w-5 text-slate-600" />
              </Button>
              
              <Select 
                value={futureMonth.toString()} 
                onValueChange={(v) => setFutureMonth(parseInt(v))}
              >
                <SelectTrigger className="w-[140px] h-12 bg-white rounded-lg border-slate-200" data-testid="future-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthNames.map(m => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={futureYear.toString()} 
                onValueChange={(v) => setFutureYear(parseInt(v))}
              >
                <SelectTrigger className="w-[100px] h-12 bg-white rounded-lg border-slate-200" data-testid="future-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {futureYears.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="icon"
                onClick={goToNextFutureMonth}
                className="h-12 w-12 bg-white rounded-lg border-slate-200"
                data-testid="future-next-month-btn"
              >
                <ChevronRight className="h-5 w-5 text-slate-600" />
              </Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl lg:rounded-sm shadow-sm overflow-hidden">
            <div className="px-4 lg:px-6 py-3 lg:py-4 bg-blue-50 border-b border-blue-200">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-blue-900">Future Rides for {monthNames.find(m => m.value === futureMonth)?.label} {futureYear} ({filteredFutureRides.length})</h3>
              </div>
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full" data-testid="rides-table-future">
                <thead>
                  <tr>
                    <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[0].left, minWidth: stickyStyles[0].minWidth }}>Customer</th>
                    <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[1].left, minWidth: stickyStyles[1].minWidth }}>Date & Time</th>
                    <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[2].left, minWidth: stickyStyles[2].minWidth }}>Route</th>
                    <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[3].left, minWidth: stickyStyles[3].minWidth }}>Driver</th>
                    <th className={`text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${stickyHeadBase}`} style={{ left: stickyStyles[4].left, minWidth: stickyStyles[4].minWidth, boxShadow: '4px 0 6px -2px rgba(0,0,0,0.08)' }}>Partner</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Vehicle</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">V. Type</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Extra</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Price</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Commission</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Tracking</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFutureRides.length === 0 ? (
                    <tr>
                      <td colSpan="13" className="px-6 py-12 text-center text-slate-500">
                        No future rides scheduled for {monthNames.find(m => m.value === futureMonth)?.label} {futureYear}
                      </td>
                    </tr>
                  ) : (
                    filteredFutureRides.map(renderRideRow)
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden p-4 space-y-4">
              {filteredFutureRides.length === 0 ? (
                <div className="py-12 text-center text-slate-500">
                  No future rides scheduled for {monthNames.find(m => m.value === futureMonth)?.label} {futureYear}
                </div>
              ) : (
                filteredFutureRides.map(renderMobileRideCard)
              )}
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ride</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this ride? This action cannot be undone.
              {rideToDelete && (
                <div className="mt-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
                  <p className="text-sm text-slate-900 font-medium">
                    {rideToDelete.customer_name || 'Unnamed Customer'}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    {rideToDelete.pickup_location} → {rideToDelete.dropoff_location}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Ride
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Ride Tracking</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset all tracking data for this ride? This will:
              <ul className="mt-3 list-disc list-inside space-y-1 text-sm">
                <li>Clear &quot;Arrived at Pickup&quot; timestamp</li>
                <li>Clear &quot;Picked Up Customer&quot; timestamp</li>
                <li>Clear &quot;Arrived at Dropoff&quot; timestamp</li>
                <li>Set status back to &quot;ACTIVE&quot;</li>
              </ul>
              {rideToReset && (
                <div className="mt-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
                  <p className="text-sm text-slate-900 font-medium">
                    {rideToReset.customer_name || 'Unnamed Customer'}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">
                    {rideToReset.pickup_location} → {rideToReset.dropoff_location}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={resetTracking}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              Reset Tracking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Vehicle Types Dialog */}
      <Dialog open={showManageVehicleTypes} onOpenChange={(open) => { if (!savingVehicleTypes) setShowManageVehicleTypes(open); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="h-5 w-5 text-indigo-600" />
              Manage Vehicle Types
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500">Add, rename or remove vehicle types. Changes apply to all new rides; existing rides keep their stored value.</p>

            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {vehicleTypesDraft.length === 0 ? (
                <p className="text-sm text-slate-400 italic text-center py-2">No vehicle types yet</p>
              ) : vehicleTypesDraft.map((vt, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1.5">
                  <Input
                    value={vt}
                    onChange={(e) => {
                      const next = [...vehicleTypesDraft];
                      next[idx] = e.target.value;
                      setVehicleTypesDraft(next);
                    }}
                    className="h-8 text-sm"
                    data-testid={`vehicle-type-input-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => setVehicleTypesDraft(vehicleTypesDraft.filter((_, i) => i !== idx))}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm"
                    title="Remove"
                    data-testid={`remove-vehicle-type-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <Input
                value={newVehicleTypeInput}
                onChange={(e) => setNewVehicleTypeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const v = newVehicleTypeInput.trim();
                    if (!v) return;
                    if (vehicleTypesDraft.some(t => t.toLowerCase() === v.toLowerCase())) {
                      toast.error('Already exists');
                      return;
                    }
                    setVehicleTypesDraft([...vehicleTypesDraft, v]);
                    setNewVehicleTypeInput('');
                  }
                }}
                placeholder="New vehicle type (e.g. Minibus, Electric)"
                className="h-9 text-sm"
                data-testid="new-vehicle-type-input"
              />
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  const v = newVehicleTypeInput.trim();
                  if (!v) return;
                  if (vehicleTypesDraft.some(t => t.toLowerCase() === v.toLowerCase())) {
                    toast.error('Already exists');
                    return;
                  }
                  setVehicleTypesDraft([...vehicleTypesDraft, v]);
                  setNewVehicleTypeInput('');
                }}
                className="h-9 bg-indigo-600 hover:bg-indigo-700"
                data-testid="add-vehicle-type-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowManageVehicleTypes(false)} disabled={savingVehicleTypes}>
              Cancel
            </Button>
            <Button
              onClick={saveVehicleTypes}
              disabled={savingVehicleTypes || vehicleTypesDraft.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-vehicle-types-btn"
            >
              {savingVehicleTypes ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ride Detail Modal */}
      <RideDetailModal
        ride={selectedRideForDetail}
        drivers={drivers}
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedRideForDetail(null);
        }}
        onRideUpdated={async () => {
          // Refresh the ride data after photos are added
          await fetchData();
          // Also refresh the selected ride
          if (selectedRideForDetail) {
            try {
              const response = await axios.get(`${API}/rides/${selectedRideForDetail.id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              setSelectedRideForDetail(response.data);
            } catch (error) {
              console.error('Error refreshing ride:', error);
            }
          }
        }}
      />
    </div>
  );
}
